import { arbitrum, mainnet } from "wagmi/chains";
import type { Address } from "viem";

export const LOCK_DURATION_OPTIONS = [
    { value: 1, label: "1 week" },
    { value: 2, label: "2 week" },
    { value: 4, label: "4 week" },
    { value: 6, label: "6 week" },
    { value: 8, label: "8 week" },
    { value: 13, label: "13 week" },
] as const;

export const ALLOWED_LOCK_DURATION_VALUES = [1, 2, 4, 6, 8, 13];

export const LOCK_INITIAL_DURATION = 8;
export const LOCK_MIN_DURATION = 1;
export const LOCK_MAX_DURATION = 13;

export const LOCK_TOKEN_DECIMALS = 18;

type LockDurationKey = `${number}w`;

// Lock contract addresses by network
export const LOCK_ADDRESSES: Record<number, Record<LockDurationKey, Address>> = {
    [arbitrum.id]: {
        "1w": "0xF988761C607e65Fc9f14c849e44cd3F1a096080f", // LOCKED_POSITION_TOKEN_1
        "2w": "0x2E8535962bbB6cb85a77713D52ca027Bb51a387A", // LOCKED_POSITION_TOKEN_2
        "3w": "0x6Ad73c6CeE6FC1919BF9FC3d72c5a15C8F5D133E", // LOCKED_POSITION_TOKEN_3
        "4w": "0x7552326CCfe3D8FDF57ba270D97eBe8Dd6CdfDD0", // LOCKED_POSITION_TOKEN_4
        "5w": "0x848d71efDBd8cBe05DaC9f780075a4592Fa322af", // LOCKED_POSITION_TOKEN_5
        "6w": "0x7de6c353846a3F34177Ef47Cf96522468A895B61", // LOCKED_POSITION_TOKEN_6
        "7w": "0x718Df244c21B09473C5153C508E20580a6f7eBba", // LOCKED_POSITION_TOKEN_7
        "8w": "0x5566b25f77D0dC831e9BaB7667BDd02729430a79", // LOCKED_POSITION_TOKEN_8
        "9w": "0xA49372f944Fa0d7EfF2d290315fd861530A68E0f", // LOCKED_POSITION_TOKEN_9
        "10w": "0x394A24227ED81617b67fafCCFEd160e8C322EbEB", // LOCKED_POSITION_TOKEN_10
        "11w": "0x7Ae4F454A797c1EbE6F25F703149B25fdf4ACb06", // LOCKED_POSITION_TOKEN_11
        "12w": "0x907E718ea7f2D091160cC7cf3a5bb2bfE5A89182", // LOCKED_POSITION_TOKEN_12
        "13w": "0xDd35B38C13c1Daf9EFA079c47959D074285882c1", // LOCKED_POSITION_TOKEN_13
    },
    [mainnet.id]: {
        "1w": "0x12b004719fb632f1E7c010c6F5D6009Fb4258442", // LOCKED_POSITION_TOKEN_1
        "2w": "0xf1839BeCaF586814D022F16cDb3504ff8D8Ff361", // LOCKED_POSITION_TOKEN_2
        "3w": "0xed2a360FfDC1eD4F8df0bd776a1FfbbE06444a0A", // LOCKED_POSITION_TOKEN_3
        "4w": "0x66bCF6151D5558AfB47c38B20663589843156078", // LOCKED_POSITION_TOKEN_4
        "5w": "0xf0c4A78fEbf4062aeD39A02BE8a4C72E9857d7d1", // LOCKED_POSITION_TOKEN_5
        "6w": "0xb06Cc4548FebfF3D66a680F9c516381c79bC9707", // LOCKED_POSITION_TOKEN_6
        "7w": "0x3A744A6b57984eb62AeB36eB6501d268372cF8bb", // LOCKED_POSITION_TOKEN_7
        "8w": "0xf68b95b7e851170c0e5123a3249dD1Ca46215085", // LOCKED_POSITION_TOKEN_8
        "9w": "0xBB5cA732fAfEd8870F9C0e8406Ad707939c912E1", // LOCKED_POSITION_TOKEN_9
        "10w": "0xd15fbf48c6dDdADC9Ef0693B060d80aF51cC26d5", // LOCKED_POSITION_TOKEN_10
        "11w": "0xed030a37Ec6EB308A416Dc64dD4b649A2BBE4FCd", // LOCKED_POSITION_TOKEN_11
        "12w": "0x3D360aB96B942c1251Ab061178F731eFEbc2d644", // LOCKED_POSITION_TOKEN_12
        "13w": "0xbd3f9814eB946E617f1d774A6762cDbec0bf087A", // LOCKED_POSITION_TOKEN_13
    },
} as const;

export const LOCK_CONTROLLER_ADDRESSES: Record<number, Address> = {
    [arbitrum.id]: "0xB6EAfb17f2A7De9f79C07C38D423bc26ed8B6415", // LOCKING_CONTROLLER
    [mainnet.id]: "0x1d95cC100D6Cd9C7BbDbD7Cb328d99b3D6037fF7", // LOCKING_CONTROLLER
} as const;

export const getLockDurationKey = (chainId: number, duration: number): LockDurationKey => {
    const lockAddress = LOCK_ADDRESSES[chainId]?.[`${duration}w`];
    if (!lockAddress) {
        throw new Error(`Invalid lock duration: ${duration}`);
    }
    return `${duration}w` as LockDurationKey;
};

// Helper function to get lock address for a specific duration
export const getLockAddress = (chainId: number, duration: number): Address => {
    const durationKey = getLockDurationKey(chainId, duration);

    if (LOCK_ADDRESSES[chainId]?.[durationKey]) {
        return LOCK_ADDRESSES[chainId][durationKey] as Address;
    }

    throw new Error(`Lock address not found for duration ${duration} on chain ID ${chainId}`);
};
